import React, { useCallback, useEffect, useRef, useState } from "react"
import icon_autoRotate from "./assets/autoRotate.svg"
import icon_viste from "./assets/viste.svg"
import icon_vr from "./assets/vr.svg"
import icon_screenshot from "./assets/screenshot.svg"
import icon_measure from "./assets/measure.svg"
import icon_info from "./assets/info.svg"
import icon_share from "./assets/share.svg"
import icon_pdf from "./assets/pdf.svg"
import { getTranslations } from "./components/translations"

function Player({
  productName,
  selectedProductCode,
  selectedProduct,
  selectedMaterials = {},
  viewerMaterials = [],
  currentLanguage,
  language,
  isAdmin = false,
  onInfoClick,
  isInfoSectionVisible = false,
  isSidebarVisible = true,
}) {
  const emersyaIframeRef = useRef(null)
  const perspectiveMenuRef = useRef(null)
  const [viewerActive, setViewerActive] = useState(false)
  const [showViewMenu, setShowViewMenu] = useState(false)
  const [customViews, setCustomViews] = useState([]) // CRITICAL: Always initialize as empty array
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false) // Mobile vertical menu state
  const [hasTrigger, setHasTrigger] = useState(false) // Animation trigger exists
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(false) // Animation is playing
  const triggerTimeoutRef = useRef(null) // Timeout reference for auto-reset

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        perspectiveMenuRef.current &&
        !perspectiveMenuRef.current.contains(event.target)
      ) {
        setShowViewMenu(false)
      }
    }

    if (showViewMenu) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showViewMenu])

  // Load custom views when product changes
  useEffect(() => {
    if (selectedProduct) {
      try {
        loadCustomViews()
      } catch (error) {
        console.error("Error loading custom views:", error)
        setCustomViews([]) // Reset to empty array on error
      }
    } else {
      setCustomViews([]) // Clear views when no product selected
    }
  }, [selectedProduct, isAdmin]) // Added isAdmin as dependency to refresh when role changes

  // Initialize Emersya viewer
  useEffect(() => {
    // Destroy previous viewer before creating a new one to prevent WebGL context leaks
    if (window.emViewers && window.emViewers["emersyaIframe"]) {
      try {
        console.log("[Emersya] Destroying previous viewer before loading new product")
        window.emViewers["emersyaIframe"].destroyViewer()
      } catch (err) {
        console.warn("[Emersya] Error destroying previous viewer:", err)
      }
    }

    const script = document.createElement("script")
    script.type = "text/javascript"
    script.src = "https://cdn.emersya.com/f/emersyaLoader.js"
    script.id = "emersya"

    script.setAttribute("routing", selectedProductCode)
    script.setAttribute("containerId", "emersyaIframe")

    // Remove any existing emersya script before appending new one
    const existingScript = document.getElementById("emersya")
    if (existingScript) {
      existingScript.remove()
    }

    document.head.appendChild(script)

    function initializeEmersyaAPI() {
      const emersyaViewer = emViewers["emersyaIframe"]
      console.log(emersyaViewer)
      setViewerActive(true)
    }

    document.addEventListener(
      "emersyaViewerInitialized",
      initializeEmersyaAPI,
      false,
    )

    return () => {
      document.removeEventListener(
        "emersyaViewerInitialized",
        initializeEmersyaAPI,
      )
      // Destroy viewer on cleanup to free WebGL context and GPU memory
      if (window.emViewers && window.emViewers["emersyaIframe"]) {
        try {
          console.log("[Emersya] Destroying viewer on cleanup")
          window.emViewers["emersyaIframe"].destroyViewer()
        } catch (err) {
          console.warn("[Emersya] Error destroying viewer on cleanup:", err)
        }
      }
      if (script.parentNode) {
        script.parentNode.removeChild(script)
      }
      setViewerActive(false)
    }
  }, [selectedProductCode])

  // Check for animation states via Emersya API after viewer loads
  useEffect(() => {
    if (!viewerActive) {
      setHasTrigger(false)
      return
    }

    let attempts = 0
    const maxAttempts = 20 // Try for up to 20 seconds
    let intervalId = null

    const checkForAnimations = async () => {
      try {
        const emersyaViewer = window.emViewers?.["emersyaIframe"]

        // Debug: log what we have
        if (attempts === 0) {
          console.log("DEBUG - emViewers:", window.emViewers)
          console.log("DEBUG - emersyaViewer:", emersyaViewer)
          if (emersyaViewer) {
            console.log(
              "DEBUG - available methods:",
              Object.keys(emersyaViewer),
            )
          }
        }

        if (!emersyaViewer) {
          attempts++
          if (attempts >= maxAttempts && intervalId) {
            clearInterval(intervalId)
            console.log(
              "Emersya viewer not available after",
              maxAttempts,
              "seconds",
            )
          }
          return
        }

        // Try to get animation states
        if (emersyaViewer.getCurrentAnimationStates) {
          const data = await emersyaViewer.getCurrentAnimationStates()
          console.log("DEBUG - getCurrentAnimationStates result:", data)

          if (data && data.states && data.states.length > 0) {
            setHasTrigger(true)
            if (intervalId) clearInterval(intervalId)
            console.log("✓ Animation states found:", data.states)
            return
          }
        }

        // Fallback: check for trigger DOM element
        const triggerElement = document.getElementById("trigger_1")
        if (triggerElement) {
          setHasTrigger(true)
          if (intervalId) clearInterval(intervalId)
          console.log("✓ Trigger DOM element found")
          return
        }

        attempts++
        if (attempts >= maxAttempts) {
          setHasTrigger(false)
          if (intervalId) clearInterval(intervalId)
          console.log(
            "No animations/triggers found after",
            maxAttempts,
            "seconds",
          )
        }
      } catch (error) {
        console.warn("Error checking animations:", error)
        attempts++
        if (attempts >= maxAttempts && intervalId) {
          clearInterval(intervalId)
        }
      }
    }

    // Start checking after 3 seconds, then every 1 second
    const startTimeout = setTimeout(() => {
      checkForAnimations()
      intervalId = setInterval(checkForAnimations, 1000)
    }, 3000)

    return () => {
      clearTimeout(startTimeout)
      if (intervalId) clearInterval(intervalId)
    }
  }, [viewerActive, selectedProductCode])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (triggerTimeoutRef.current) {
        clearTimeout(triggerTimeoutRef.current)
      }
    }
  }, [])

  // Function to trigger animation via Emersya API and auto-reset after 7 seconds
  const handleTriggerAnimation = useCallback(async () => {
    const emersyaViewer = window.emViewers?.["emersyaIframe"]
    if (!emersyaViewer || !emersyaViewer.triggerAnimation) {
      console.warn("Emersya viewer or triggerAnimation not available")
      return
    }

    try {
      // Trigger the animation
      await emersyaViewer.triggerAnimation({ triggerId: 1 })
      setIsAnimationPlaying(true)
      console.log("Animation triggered")

      // Clear any existing timeout
      if (triggerTimeoutRef.current) {
        clearTimeout(triggerTimeoutRef.current)
      }

      // Auto-reset after 6 seconds
      triggerTimeoutRef.current = setTimeout(async () => {
        try {
          await emersyaViewer.triggerAnimation({ triggerId: 1 })
          console.log("Animation reset triggered")
        } catch (error) {
          console.warn("Error resetting animation:", error)
        }
        setIsAnimationPlaying(false)
      }, 6000)
    } catch (error) {
      console.error("Error triggering animation:", error)
      setIsAnimationPlaying(false)
    }
  }, [])

  // UPDATED: Load custom views from both product and family with safety checks
  const loadCustomViews = () => {
    const views = []

    // Add product-specific custom views
    if (
      selectedProduct?.customViews &&
      Array.isArray(selectedProduct.customViews)
    ) {
      selectedProduct.customViews.forEach((view) => {
        // CRITICAL: Add safety checks for view object and properties
        if (!view || typeof view !== "object" || !view.label) {
          console.warn("Invalid product view detected:", view)
          return
        }

        // CORRECTED: Filter based on enabledForUsers for non-admin users
        if (isAdmin || view.enabledForUsers) {
          views.push({
            ...view,
            source: "product",
            label: view.label,
            sourceType: "Prodotto", // For display purposes
          })
        }
      })
    }

    // Add family-specific custom views
    if (
      selectedProduct?._familyDefaults?.customViews &&
      Array.isArray(selectedProduct._familyDefaults.customViews)
    ) {
      selectedProduct._familyDefaults.customViews.forEach((view) => {
        // CRITICAL: Add safety checks for view object and properties
        if (!view || typeof view !== "object" || !view.label) {
          console.warn("Invalid family view detected:", view)
          return
        }

        // CORRECTED: Filter based on enabledForUsers for non-admin users
        if (isAdmin || view.enabledForUsers) {
          views.push({
            ...view,
            source: "family",
            label: `${view.label} (Famiglia)`,
            sourceType: "Famiglia", // For display purposes
          })
        }
      })
    }

    console.log(`Loaded ${views.length} custom views for player:`, views)
    setCustomViews(views)
  }

  // Define preset camera views - UPDATED with admin/user distinction
  const getPresetViews = () => {
    const allViews = {
      // VISTE FRONTALI
      "1A. TAVOLO PICCOLO": {
        position: [0.08793692290782928, 1.495231032371521, 4.501952648162842],
        target: [0, 0.37112775444984436, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "1A. TAVOLO PICCOLO",
        adminOnly: true,
      },
      "1B. TAVOLO GRANDE": {
        position: [
          -0.0039832619950175285, 1.2964057922363281, 5.9750800132751465,
        ],
        target: [0, 0.37262773513793945, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "1B. TAVOLO GRANDE",
        adminOnly: true,
      },
      "2A. SEDUTE": {
        position: [0.00975882913917303, 0.7702409029006958, 4.619032859802246],
        target: [0, 0.36612701416015625, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "2A. SEDUTE",
        adminOnly: true,
      },
      "2B. LIBRERIA": {
        position: [
          -0.008066613227128983, 0.5714938640594482, 3.3638663291931152,
        ],
        target: [
          -0.039580151438713074, 0.5714938640594482, -0.04686998575925827,
        ],
        up: [0, 1, 0],
        fov: 35,
        label: "2B. LIBRERIA",
        adminOnly: true,
      },
      FRONTALE: {
        // Renamed from "2C. LIBRERIA GRANDE"
        position: [-0.032389383763074875, 0.938500165939331, 5.911214828491211],
        target: [
          -0.039580151438713074, 0.7594552040100098, -0.04686998575925827,
        ],
        up: [0, 1, 0],
        fov: 35,
        label: "FRONTALE",
        adminOnly: false,
        originalKey: "2C. LIBRERIA GRANDE",
      },

      // VISTE LATERALI
      "3. TAVOLI SX": {
        position: [-2.229461431503296, 1.4171435832977295, 5.5214080810546875],
        target: [0, 0.3721277713775635, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "3. TAVOLI SX",
        adminOnly: true,
      },
      "3. TAVOLI DX": {
        position: [2.229461431503296, 1.4171435832977295, 5.5214080810546875],
        target: [0, 0.3721277713775635, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "3. TAVOLI DX",
        adminOnly: true,
      },
      "4. RETRO SX": {
        position: [-2.767357587814331, 0.7379804849624634, -3.666008472442627],
        target: [0, 0.3361237943172455, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "4. RETRO SX",
        adminOnly: true,
      },
      "4. RETRO DX": {
        position: [2.767357587814331, 0.7379804849624634, -3.666008472442627],
        target: [0, 0.3361237943172455, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "4. RETRO DX",
        adminOnly: true,
      },
      "5A. SEDUTE SX": {
        position: [-3.4535088539123535, 0.8563621044158936, 4.4126434326171875],
        target: [0, 0.36612772941589355, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "5A. SEDUTE SX",
        adminOnly: true,
      },
      "5A. SEDUTE DX": {
        position: [3.4535088539123535, 0.8563621044158936, 4.4126434326171875],
        target: [0, 0.36612772941589355, -5.960464477539063e-8],
        up: [0, 1, 0],
        fov: 30,
        label: "5A. SEDUTE DX",
        adminOnly: true,
      },
      "5B. LIBRERIE SX": {
        position: [-1.86250901222229, 0.5714938640594482, 2.8582515716552734],
        target: [
          -0.039580151438713074, 0.5714938640594482, -0.04686998575925827,
        ],
        up: [0, 1, 0],
        fov: 35,
        label: "5B. LIBRERIE SX",
        adminOnly: true,
      },
      "5B. LIBRERIE DX": {
        position: [1.86250901222229, 0.5714938640594482, 2.8582515716552734],
        target: [
          0.039580151438713074, 0.5714938640594482, -0.04686998575925827,
        ],
        up: [0, 1, 0],
        fov: 35,
        label: "5B. LIBRERIE DX",
        adminOnly: true,
      },
      SINISTRA: {
        // Renamed from "5C. LIBRERIE GRANDI SX"
        position: [-3.143932819366455, 1.0718764066696167, 4.558107376098633],
        target: [
          -0.024390466511249542, 1.0718764066696167, 0.013911008834838867,
        ],
        up: [0, 1, 0],
        fov: 35,
        label: "SINISTRA",
        adminOnly: false,
        originalKey: "5C. LIBRERIE GRANDI SX",
      },
      DESTRA: {
        // Renamed from "5C. LIBRERIE GRANDI DX"
        position: [3.143932819366455, 1.0718764066696167, 4.558107376098633],
        target: [
          0.024390466511249542, 1.0718764066696167, 0.013911008834838867,
        ],
        up: [0, 1, 0],
        fov: 35,
        zoom: 0.4318633862569692,
        label: "DESTRA",
        adminOnly: false,
        originalKey: "5C. LIBRERIE GRANDI DX",
      },
    }

    // Filter views based on admin status
    if (isAdmin) {
      return allViews // Show all views for admin
    } else {
      // Show only non-admin views for regular users
      const userViews = {}
      Object.keys(allViews).forEach((key) => {
        if (!allViews[key].adminOnly) {
          userViews[key] = allViews[key]
        }
      })
      return userViews
    }
  }

  // UPDATED: Camera view handling function with improved custom view support and safety checks
  const handleCameraViewClick = useCallback((view) => {
    if (!window.emViewers || !window.emViewers["emersyaIframe"]) {
      alert("Visualizzatore 3D non disponibile")
      return
    }

    // CRITICAL: Add safety check for view object
    if (!view || typeof view !== "object") {
      console.error("Invalid view object:", view)
      alert("Vista non valida")
      return
    }

    const emersyaViewer = window.emViewers["emersyaIframe"]

    try {
      // Handle custom views (product and family)
      if (view.source) {
        // Validate custom view data
        if (!view.cameraPosition || !view.cameraTarget) {
          console.error("Invalid custom view data:", view)
          alert("Dati vista personalizzata non validi")
          return
        }

        // Get animation duration - default to 1.5 seconds if not specified
        const animationDuration = view.animationDuration || 1.5
        const transitionTime = view.animate ? animationDuration * 1000 : 500

        emersyaViewer.setCamera({
          camera: {
            position: [
              view.cameraPosition.x || 0,
              view.cameraPosition.y || 0,
              view.cameraPosition.z || 0,
            ],
            target: [
              view.cameraTarget.x || 0,
              view.cameraTarget.y || 0,
              view.cameraTarget.z || 0,
            ],
            up: [0, 1, 0],
            transitionTime: transitionTime,
            fov: view.cameraFOV || 45,
          },
        })
        console.log(`Applied custom view: ${view.label}`, {
          position: view.cameraPosition,
          target: view.cameraTarget,
          fov: view.cameraFOV,
          animated: view.animate,
          duration: animationDuration,
        })
      } else {
        // Handle preset views
        if (!view.position || !view.target) {
          console.error("Invalid preset view data:", view)
          alert("Dati vista preset non validi")
          return
        }

        emersyaViewer.setCamera({
          camera: {
            position: view.position,
            target: view.target,
            up: view.up || [0, 1, 0],
            transitionTime: 500,
            fov: view.fov || 45,
          },
        })
        console.log("Applied preset view:", view.label)
      }

      setShowViewMenu(false)
    } catch (error) {
      console.error("Error applying camera view:", error)
      alert("Errore nell'applicazione della vista camera")
    }
  }, [])

  const zoomIn = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"]
    emersyaViewer.startZoomIn().then(() => {
      setTimeout(() => {
        emersyaViewer.stopZoomIn()
      }, 800)
    })
  }, [])

  const zoomOut = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"]
    emersyaViewer.startZoomOut().then(() => {
      setTimeout(() => {
        emersyaViewer.stopZoomOut()
      }, 600)
    })
  }, [])

  const playRotation = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"]
    emersyaViewer.play()
  }, [])

  const rulers = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"]
    emersyaViewer.toggleRulers({ system: "metric" })
  })

  const resetCamera = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"]
    emersyaViewer.resetCamera({})
    emersyaViewer.play()
  }, [])

  const getScreenshot = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"]
    const date = new Date()
    const formattedDate = `${date.getFullYear()}-${
      date.getMonth() + 1
    }-${date.getDate()}`

    emersyaViewer.getCamera().then((cameraData) => {
      emersyaViewer
        .getScreenshot({
          width: 1024,
          height: 1024,
          transparentBackground: false,
          camera: cameraData,
        })
        .then((imageData) => {
          const downloadLink = document.createElement("a")
          downloadLink.href = imageData
          downloadLink.download = `${formattedDate}-${productName}.png`
          document.body.appendChild(downloadLink)
          downloadLink.click()
          document.body.removeChild(downloadLink)
        })
    })
  }, [productName])

  const getCameraData = useCallback(() => {
    const emersyaViewer = emViewers["emersyaIframe"]

    emersyaViewer
      .getCamera()
      .then((camera) => {
        // Validate camera data
        if (!camera || !camera.position || !camera.target) {
          console.error("Invalid camera data received:", camera)
          return
        }

        const formattedData = {
          position: Array.from(camera.position),
          target: Array.from(camera.target),
          fov: camera.fov || 45,
          zoom: camera.zoomProportion || 1,
        }

        const output = JSON.stringify(formattedData, null, 2)

        const dialog = document.createElement("div")
        dialog.style.position = "fixed"
        dialog.style.top = "0"
        dialog.style.left = "0"
        dialog.style.width = "100%"
        dialog.style.height = "100%"
        dialog.style.backgroundColor = "rgba(0,0,0,0.5)"
        dialog.style.zIndex = "1000"
        dialog.style.display = "flex"
        dialog.style.alignItems = "center"
        dialog.style.justifyContent = "center"

        const content = document.createElement("div")
        content.style.background = "white"
        content.style.padding = "20px"
        content.style.borderRadius = "8px"
        content.style.maxWidth = "500px"
        content.style.width = "90%"

        const pre = document.createElement("pre")
        pre.style.marginBottom = "15px"
        pre.style.whiteSpace = "pre-wrap"
        pre.textContent = output

        const copyButton = document.createElement("button")
        copyButton.textContent = "Copy Data"
        copyButton.style.padding = "8px 16px"
        copyButton.style.background = "#007bff"
        copyButton.style.color = "white"
        copyButton.style.border = "none"
        copyButton.style.borderRadius = "4px"
        copyButton.style.cursor = "pointer"
        copyButton.style.marginRight = "10px"
        copyButton.onclick = () => {
          navigator.clipboard.writeText(output).then(() => {
            copyButton.textContent = "Copied!"
            setTimeout(() => {
              copyButton.textContent = "Copy Data"
            }, 2000)
          })
        }

        const closeButton = document.createElement("button")
        closeButton.textContent = "Close"
        closeButton.style.padding = "8px 16px"
        closeButton.style.background = "#6c757d"
        closeButton.style.color = "white"
        closeButton.style.border = "none"
        closeButton.style.borderRadius = "4px"
        closeButton.style.cursor = "pointer"
        closeButton.onclick = () => dialog.remove()

        content.appendChild(pre)
        content.appendChild(copyButton)
        content.appendChild(closeButton)
        dialog.appendChild(content)
        document.body.appendChild(dialog)
      })
      .catch((error) => {
        console.error("Failed to get camera data:", error)
        alert("Errore nel recupero dei dati camera")
      })
  }, [viewerActive])

  const getImageUrl = (thumbnail) => {
    if (thumbnail && thumbnail.startsWith("#")) return thumbnail
    if (
      thumbnail &&
      (thumbnail.startsWith("http") || thumbnail.startsWith("//"))
    )
      return thumbnail
    if (thumbnail) {
      const cleanPath = thumbnail.replace(/^\.\//, "")
      return `https://moroso-ayea.ensoul.worsk/${cleanPath}`
    }
    return ""
  }

  // Funzione per nome prodotto localizzato (come FamilyAccordion)
  const getLocalizedProductName = (product, language) => {
    if (product?.lang?.[language]) {
      return product.lang[language]
    }
    return product?.name || "N/A"
  }

  // Funzione per tradurre tipi materiali (come Accordion)
  const getTranslatedTitle = (
    originalTitle,
    selectedProduct,
    translations,
    language,
  ) => {
    // Prima prova con le traduzioni specifiche della famiglia (tipi tecnici)
    if (
      selectedProduct?._familyDefaults?.technicalTypeTranslations?.[
        originalTitle
      ]?.[language]
    ) {
      return selectedProduct._familyDefaults.technicalTypeTranslations[
        originalTitle
      ][language]
    }

    // Fallback alle traduzioni globali
    if (translations.materialTypes?.[originalTitle.toLowerCase()]) {
      return translations.materialTypes[originalTitle.toLowerCase()]
    }

    // Fallback al titolo originale
    return originalTitle
  }

  const getVisibleSelectedMaterials = (selectedMaterials, viewerMaterials) => {
    if (!selectedMaterials || !viewerMaterials) return {}

    const visibleMaterials = {}

    // Filtra viewerMaterials per ottenere solo quelli visibili (>= 2 varianti)
    const visibleMaterialTypes = viewerMaterials
      .filter((mat) => {
        return mat.variations.length >= 2 // Solo materiali con abbastanza varianti
      })
      .map((mat) => mat.technicalName)

    // Include solo i materiali selezionati che sono in tipi di materiali visibili
    Object.entries(selectedMaterials).forEach(([type, material]) => {
      if (visibleMaterialTypes.includes(type)) {
        visibleMaterials[type] = material
      }
    })

    return visibleMaterials
  }

  const generatePDF = useCallback(
    async (includeForm = false, contactData = null) => {
      const { jsPDF } = await import("jspdf")
      const emersyaViewer = emViewers["emersyaIframe"]

      try {
        // Ottieni traduzioni per la lingua corrente
        const translations = getTranslations(language || "it")
        const pdfT = translations.pdf || {}

        const cameraData = await emersyaViewer.getCamera()
        const imageData = await emersyaViewer.getScreenshot({
          width: 1024,
          height: 1024,
          transparentBackground: false,
          camera: cameraData,
        })

        const pdf = new jsPDF("p", "mm", "a4")
        const pageWidth = 210

        // ========== LAYOUT GRID (matching design) ==========
        const leftMargin = 15
        const rightMargin = 15
        const valueColX = leftMargin + 25 // Values start here

        // Image dimensions (larger, ~55% of content width)
        const imageWidth = 100
        const imageHeight = 115
        const imageX = pageWidth - rightMargin - imageWidth

        // ========== HEADER ==========
        let yPosition = 22

        // Logo a sinistra
        try {
          const logoPath = "./assets/logo-nudesign.png"
          pdf.addImage(logoPath, "PNG", leftMargin, yPosition - 6, 40, 9)
        } catch (error) {
          pdf.setFontSize(22)
          pdf.setFont(undefined, "normal")
          pdf.text("nudesign", leftMargin, yPosition)
        }

        // Company info a destra (right-aligned, touching right margin)
        const companyInfoRightEdge = pageWidth - rightMargin
        pdf.setFontSize(7)
        pdf.setFont(undefined, "normal")

        // Company name with underline (right-aligned)
        const companyName = pdfT.companyName || "nudesign srl"
        let companyNameWidth = pdf.getTextWidth(companyName)
        pdf.text(
          companyName,
          companyInfoRightEdge - companyNameWidth,
          yPosition - 6,
        )
        pdf.setDrawColor(0, 0, 0)
        pdf.setLineWidth(0.2)
        pdf.line(
          companyInfoRightEdge - companyNameWidth,
          yPosition - 5,
          companyInfoRightEdge,
          yPosition - 5,
        )

        // Other company info (right-aligned)
        const companyLines = [
          pdfT.email || "info@nudesign.it",
          pdfT.website || "www.nudesign.it",
          pdfT.phone || "t +39 0432 92386",
        ]
        companyLines.forEach((line, i) => {
          const w = pdf.getTextWidth(line)
          pdf.text(line, companyInfoRightEdge - w, yPosition - 1 + i * 3.5)
        })

        // Header divider line
        yPosition += 12
        pdf.setDrawColor(180, 180, 180)
        pdf.setLineWidth(0.3)
        pdf.line(leftMargin, yPosition, pageWidth - rightMargin, yPosition)
        yPosition += 15

        // ========== MAIN CONTENT ==========
        const contentStartY = yPosition

        // Fixed width for underlines (extends past values)
        const underlineEndX = imageX - 15

        // Helper: Draw row with label and value, underline spans from label past value
        const drawRow = (label, value, y, valueFontSize = 11) => {
          // Draw label
          pdf.setFontSize(7)
          pdf.setFont(undefined, "bold")
          pdf.text(label, leftMargin, y)

          // Draw value
          pdf.setFontSize(valueFontSize)
          pdf.setFont(undefined, "normal")
          pdf.text(value, valueColX, y)

          // Draw long underline from label start past the value
          pdf.setDrawColor(0, 0, 0)
          pdf.setLineWidth(0.2)
          pdf.line(leftMargin, y + 1.5, underlineEndX, y + 1.5)
        }

        // ========== COLLEZIONE ==========
        const familyName =
          selectedProduct?.family || selectedProduct?._family || ""
        drawRow(pdfT.collection || "COLLEZIONE", familyName, yPosition)
        yPosition += 8

        // ========== CODICE ==========
        const productCode =
          selectedProduct?.code || selectedProduct?.id || "N/A"
        drawRow(pdfT.code || "CODICE", productCode, yPosition)
        yPosition += 10

        // ========== FINITURE (Materials) ==========
        const visibleSelectedMaterials = getVisibleSelectedMaterials(
          selectedMaterials,
          viewerMaterials,
        )

        if (
          visibleSelectedMaterials &&
          Object.keys(visibleSelectedMaterials).length > 0
        ) {
          const swatchSize = 5
          const rowHeight = 7
          const swatchX = valueColX
          const textAfterSwatchX = swatchX + swatchSize + 2
          const materialCodeX = textAfterSwatchX + 28

          const materialEntries = Object.entries(visibleSelectedMaterials)
          const lastIndex = materialEntries.length - 1

          materialEntries.forEach(([type, material], index) => {
            const rowY = yPosition + index * rowHeight
            if (rowY > 200) return

            const swatchY = rowY - 1

            // Draw small swatch
            if (material.thumbnail) {
              try {
                const imageUrl = getImageUrl(material.thumbnail)
                if (imageUrl.startsWith("#")) {
                  const hex = imageUrl.replace("#", "")
                  const r = parseInt(hex.substring(0, 2), 16)
                  const g = parseInt(hex.substring(2, 4), 16)
                  const b = parseInt(hex.substring(4, 6), 16)
                  pdf.setFillColor(r, g, b)
                  pdf.rect(swatchX, swatchY, swatchSize, swatchSize, "F")
                } else if (imageUrl) {
                  pdf.addImage(
                    imageUrl,
                    "PNG",
                    swatchX,
                    swatchY,
                    swatchSize,
                    swatchSize,
                  )
                }
              } catch (error) {
                pdf.setFillColor(200, 200, 200)
                pdf.rect(swatchX, swatchY, swatchSize, swatchSize, "F")
              }
            } else {
              pdf.setFillColor(200, 200, 200)
              pdf.rect(swatchX, swatchY, swatchSize, swatchSize, "F")
            }

            // Material type label
            const translatedType = getTranslatedTitle(
              type,
              selectedProduct,
              translations,
              language || "it",
            )
            pdf.setFontSize(9)
            pdf.setFont(undefined, "normal")
            pdf.text(`${translatedType}:`, textAfterSwatchX, rowY + 2.5)

            // Material code/name
            const materialName =
              material.displayName ||
              material.name ||
              material.viewerName ||
              "N/A"
            pdf.text(materialName, materialCodeX, rowY + 2.5)

            // Draw FINITURE label with underline on the last material row
            if (index === lastIndex) {
              const finitureY = rowY + 6 // Add margin top
              pdf.setFontSize(7)
              pdf.setFont(undefined, "bold")
              pdf.text(pdfT.finishes || "FINITURE", leftMargin, finitureY)
              pdf.setDrawColor(0, 0, 0)
              pdf.setLineWidth(0.2)
              // Long underline matching other rows
              pdf.line(
                leftMargin,
                finitureY + 1.5,
                underlineEndX,
                finitureY + 1.5,
              )
            }
          })
        }

        // ========== RIGHT COLUMN - Screenshot ==========
        const imgY = contentStartY - 5
        // Gray background
        pdf.setFillColor(240, 240, 240)
        pdf.rect(imageX, imgY, imageWidth, imageHeight, "F")
        pdf.addImage(imageData, "PNG", imageX, imgY, imageWidth, imageHeight)

        // ========== BOTTOM - Price (aligned with bottom of image) ==========
        const imageBottomY = imgY + imageHeight
        const priceY = imageBottomY // Aligned exactly with image bottom

        if (
          selectedProduct?.price &&
          typeof selectedProduct.price === "number"
        ) {
          // A PARTIRE DA label
          pdf.setFontSize(7)
          pdf.setFont(undefined, "bold")
          const priceLabel = pdfT.startingFrom || "A PARTIRE DA"
          pdf.text(priceLabel, leftMargin, priceY)

          // Long underline from label to end
          pdf.setDrawColor(0, 0, 0)
          pdf.setLineWidth(0.2)
          pdf.line(leftMargin, priceY + 1.5, underlineEndX, priceY + 1.5)

          // Price value at the RIGHT END of the underline (right-aligned)
          pdf.setFontSize(16)
          pdf.setFont(undefined, "normal")
          const priceText = `€${selectedProduct.price.toLocaleString("it-IT")}`
          const priceWidth = pdf.getTextWidth(priceText)
          pdf.text(priceText, underlineEndX - priceWidth, priceY)
        }

        // ========== CONTACT DATA (if included) ==========
        if (includeForm && contactData) {
          let clientY = Math.max(yPosition + 30, contentStartY + 120)

          pdf.setFontSize(9)
          pdf.setFont(undefined, "bold")
          pdf.text("Cliente:", leftMargin, clientY)
          pdf.setFont(undefined, "normal")

          const clientInfo = []
          if (contactData.nome || contactData.cognome) {
            clientInfo.push(
              `${contactData.nome || ""} ${contactData.cognome || ""}`.trim(),
            )
          }
          if (contactData.email) clientInfo.push(contactData.email)
          if (contactData.telefono) clientInfo.push(contactData.telefono)
          if (contactData.azienda) clientInfo.push(contactData.azienda)

          clientInfo.forEach((info, index) => {
            pdf.text(info, leftMargin + 20, clientY + 5 + index * 4)
          })
        }

        // ========== SAVE PDF ==========
        const now = new Date()
        const formattedDate = `${now.getFullYear()}-${String(
          now.getMonth() + 1,
        ).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`
        const formattedTime = `${String(now.getHours()).padStart(
          2,
          "0",
        )}:${String(now.getMinutes()).padStart(2, "0")}`
        const fileProductCode =
          selectedProduct?.code || selectedProduct?.id || "PRODOTTO"

        pdf.save(
          `${formattedDate}-${formattedTime}-${fileProductCode}-configurazione.pdf`,
        )
      } catch (error) {
        console.error("Errore nella generazione PDF:", error)
        alert("Errore nella generazione del PDF")
      }
    },
    [selectedProduct, selectedMaterials, viewerMaterials, language],
  )

  useEffect(() => {
    window.generatePDF = generatePDF
    window.getScreenshot = getScreenshot
    return () => {
      delete window.generatePDF
      delete window.getScreenshot
    }
  }, [generatePDF, getScreenshot])

  // UPDATED: Group views with custom views section and better organization
  const getViewCategories = () => {
    const presetViews = getPresetViews()
    const categories = {}

    if (isAdmin) {
      // Show all categories for admin
      categories["VISTE FRONTALI"] = [
        "1A. TAVOLO PICCOLO",
        "1B. TAVOLO GRANDE",
        "2A. SEDUTE",
        "2B. LIBRERIA",
        "FRONTALE",
      ].filter((key) => presetViews[key])

      categories["VISTE LATERALI"] = [
        "3. TAVOLI SX",
        "3. TAVOLI DX",
        "4. RETRO SX",
        "4. RETRO DX",
        "5A. SEDUTE SX",
        "5A. SEDUTE DX",
        "5B. LIBRERIE SX",
        "5B. LIBRERIE DX",
        "SINISTRA",
        "DESTRA",
      ].filter((key) => presetViews[key])
    } else {
      // Show only user views
      categories["VISTE STANDARD"] = ["FRONTALE", "SINISTRA", "DESTRA"].filter(
        (key) => presetViews[key],
      )
    }

    // UPDATED: Add custom views if any exist - with proper organization and safety checks
    if (Array.isArray(customViews) && customViews.length > 0) {
      // Separate product and family views for better organization
      const productCustomViews = customViews.filter(
        (view) => view && view.source === "product" && view.label,
      )
      const familyCustomViews = customViews.filter(
        (view) => view && view.source === "family" && view.label,
      )

      if (productCustomViews.length > 0) {
        categories["VISTE PRODOTTO"] = productCustomViews
      }

      if (familyCustomViews.length > 0) {
        categories["VISTE FAMIGLIA"] = familyCustomViews
      }

      // If we want to keep them together as before, use this instead:
      // categories["VISTE PERSONALIZZATE"] = customViews.filter(view => view && view.label)
    }

    return { categories, presetViews }
  }

  return (
    <div className="ay-w-full ay-h-full ay-relative">
      {/* Main 3D viewer iframe - full width and height of parent */}
      <div
        className="ay-h-[95vh] lg:ay-h-full ay-w-full ay-relative"
        id="emersyaIframe"
        ref={emersyaIframeRef}
      ></div>

      {/* MOBILE: Vertical menu - bottom left */}
      <div
        className="md:ay-hidden ay-fixed ay-bottom-[calc(5vh+50px)] ay-left-4 ay-z-20 ay-cursor-pointer"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {/* Single unified container */}
        <div
          className="ay-shadow-xl ay-bg-white ay-overflow-hidden ay-flex ay-flex-col"
          style={{
            width: "56px",
            borderRadius: "28px",
            transition: isMobileMenuOpen
              ? "border-radius 0.3s ease 0s"
              : "border-radius 0.3s ease 0.5s",
          }}
        >
          {/* White section with icons */}
          <div
            className="ay-bg-white ay-flex ay-flex-col ay-items-center ay-overflow-hidden"
            style={{
              maxHeight: isMobileMenuOpen ? "500px" : "0px",
              paddingTop: isMobileMenuOpen ? "12px" : "0px",
              paddingBottom: isMobileMenuOpen ? "0px" : "0px",
              transition: isMobileMenuOpen
                ? "max-height 0.5s cubic-bezier(0.4, 0, 0.2, 1) 0s, padding 0.3s ease 0s"
                : "max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1) 0.3s, padding 0.2s ease 0.4s",
            }}
          >
            {/* Info - conditional */}
            {(selectedProduct?.usageImages?.filter(Boolean).length > 0 ||
              selectedProduct?.relatedProducts?.length > 0) && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  if (onInfoClick) onInfoClick()
                  setIsMobileMenuOpen(false)
                }}
                className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                  transition: isMobileMenuOpen
                    ? "opacity 0.2s ease 0.45s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.45s"
                    : "opacity 0.15s ease 0s, transform 0.2s ease 0s",
                }}
              >
                <img
                  src={icon_info}
                  alt="Info"
                  className="ay-w-3 ay-h-3 ay-opacity-50"
                />
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation()
                playRotation()
                setIsMobileMenuOpen(false)
              }}
              className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                transition: isMobileMenuOpen
                  ? "opacity 0.2s ease 0.40s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.40s"
                  : "opacity 0.15s ease 0.03s, transform 0.2s ease 0.03s",
              }}
            >
              <img
                src={icon_autoRotate}
                alt="Ruota"
                className="ay-w-5 ay-h-5"
              />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                rulers()
                setIsMobileMenuOpen(false)
              }}
              className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                transition: isMobileMenuOpen
                  ? "opacity 0.2s ease 0.35s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.35s"
                  : "opacity 0.15s ease 0.06s, transform 0.2s ease 0.06s",
              }}
            >
              <img src={icon_measure} alt="Misure" className="ay-w-5 ay-h-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                setShowViewMenu(!showViewMenu)
              }}
              className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                transition: isMobileMenuOpen
                  ? "opacity 0.2s ease 0.30s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.30s"
                  : "opacity 0.15s ease 0.09s, transform 0.2s ease 0.09s",
              }}
            >
              <img src={icon_viste} alt="Viste" className="ay-w-5 ay-h-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                const arButton = document.querySelector(".arButtonContainer")
                if (arButton) arButton.click()
                setIsMobileMenuOpen(false)
              }}
              className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                transition: isMobileMenuOpen
                  ? "opacity 0.2s ease 0.25s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.25s"
                  : "opacity 0.15s ease 0.12s, transform 0.2s ease 0.12s",
              }}
            >
              <img src={icon_vr} alt="AR" className="ay-w-5 ay-h-5" />
            </button>

            {/* Separator */}
            <div
              className="ay-w-7 ay-h-px ay-bg-gray-300 ay-my-2"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? "scaleX(1)" : "scaleX(0)",
                transition: isMobileMenuOpen
                  ? "opacity 0.15s ease 0.20s, transform 0.2s ease 0.20s"
                  : "opacity 0.1s ease 0.15s, transform 0.15s ease 0.15s",
              }}
            />

            {/* Animation trigger button - mobile */}
            {hasTrigger && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleTriggerAnimation()
                  setIsMobileMenuOpen(false)
                }}
                className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
                style={{
                  opacity: isMobileMenuOpen ? 1 : 0,
                  transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                  transition: isMobileMenuOpen
                    ? "opacity 0.2s ease 0.18s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.18s"
                    : "opacity 0.15s ease 0.15s, transform 0.2s ease 0.15s",
                }}
              >
                <svg
                  className="ay-w-5 ay-h-5 ay-text-gray-700"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M8 5v14l11-7z" />
                </svg>
              </button>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation()
                getScreenshot()
                setIsMobileMenuOpen(false)
              }}
              className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                transition: isMobileMenuOpen
                  ? "opacity 0.2s ease 0.15s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s"
                  : "opacity 0.15s ease 0.18s, transform 0.2s ease 0.18s",
              }}
            >
              <img
                src={icon_screenshot}
                alt="Screenshot"
                className="ay-w-5 ay-h-5"
              />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                navigator.clipboard
                  .writeText(window.location.href)
                  .then(() => alert("Link copiato!"))
                setIsMobileMenuOpen(false)
              }}
              className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                transition: isMobileMenuOpen
                  ? "opacity 0.2s ease 0.10s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.10s"
                  : "opacity 0.15s ease 0.21s, transform 0.2s ease 0.21s",
              }}
            >
              <img src={icon_share} alt="Share" className="ay-w-5 ay-h-5" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                generatePDF(false)
                setIsMobileMenuOpen(false)
              }}
              className="ay-flex ay-items-center ay-justify-center ay-w-11 ay-h-11 ay-rounded-full hover:ay-bg-gray-100"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen ? "scale(1)" : "scale(0)",
                transition: isMobileMenuOpen
                  ? "opacity 0.2s ease 0.05s, transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) 0.05s"
                  : "opacity 0.15s ease 0.24s, transform 0.2s ease 0.24s",
              }}
            >
              <img src={icon_pdf} alt="PDF" className="ay-w-5 ay-h-5" />
            </button>
          </div>

          {/* Bottom toggle area - white when closed, black when open */}
          <div
            className="ay-flex ay-items-center ay-justify-center ay-flex-shrink-0 ay-rounded-full"
            style={{
              width: "56px",
              height: "56px",
              backgroundColor: isMobileMenuOpen ? "#000" : "#fff",
              transition: isMobileMenuOpen
                ? "background-color 0.25s ease 0.1s"
                : "background-color 0.25s ease 0.35s",
            }}
          >
            {/* Hamburger icon */}
            <svg
              className="ay-w-5 ay-h-5 ay-absolute"
              style={{
                opacity: isMobileMenuOpen ? 0 : 1,
                transform: isMobileMenuOpen
                  ? "rotate(90deg) scale(0.5)"
                  : "rotate(0deg) scale(1)",
                transition: isMobileMenuOpen
                  ? "opacity 0.2s ease 0s, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0s"
                  : "opacity 0.25s ease 0.45s, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s",
              }}
              fill="none"
              stroke="#4b5563"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
              />
            </svg>
            {/* X icon */}
            <svg
              className="ay-w-5 ay-h-5 ay-absolute"
              style={{
                opacity: isMobileMenuOpen ? 1 : 0,
                transform: isMobileMenuOpen
                  ? "rotate(0deg) scale(1)"
                  : "rotate(-90deg) scale(0.5)",
                transition: isMobileMenuOpen
                  ? "opacity 0.25s ease 0.15s, transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) 0.15s"
                  : "opacity 0.2s ease 0.1s, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1) 0.05s",
              }}
              fill="none"
              stroke="#fff"
              viewBox="0 0 24 24"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </div>
        </div>
      </div>

      {/* Bottom center controls - positioned higher on mobile to be above chevron, hidden on mobile */}
      <div className="ay-absolute ay-left-1/2 ay-transform ay--translate-x-1/2 ay-bottom-[calc(5vh+40px)] md:ay-bottom-8 ay-z-10 ay-hidden md:ay-flex ay-items-center ay-gap-3">
        {/* Main control bar - pill shaped */}
        <div className="ay-group/bar ay-flex ay-items-center ay-gap-1 md:ay-gap-1 ay-bg-white/95 ay-backdrop-blur-sm ay-px-2 md:ay-px-3 ay-py-1.5 md:ay-py-2 ay-rounded-full">
          {isAdmin && (
            <button
              onClick={getCameraData}
              className="ay-flex ay-items-center ay-gap-1 ay-text-[10px] ay-font-medium ay-px-2 ay-py-1 ay-bg-gray-100 ay-rounded hover:ay-bg-gray-200"
              style={{ transition: "background-color 0.15s ease" }}
              title="Get Camera Data"
            >
              CAM
            </button>
          )}

          {/* Info/Chevron button - Opens InfoSection */}
          {(selectedProduct?.usageImages?.filter(Boolean).length > 0 ||
            selectedProduct?.relatedProducts?.length > 0) && (
            <button
              onClick={() => {
                if (onInfoClick) onInfoClick()
              }}
              className="ay-flex ay-items-center ay-h-7 md:ay-h-8 ay-w-10 md:ay-w-auto ay-px-1.5 md:ay-px-2 ay-rounded hover:ay-bg-black/5"
              style={{ transition: "background-color 0.15s ease" }}
              title={isInfoSectionVisible ? "Chiudi Info" : "Apri Info"}
            >
              <img
                src={icon_info}
                alt="Info"
                className="ay-w-5 ay-h-5 ay-flex-shrink-0"
              />
              <span className="ay-bar-label ay-text-[10px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 md:group-hover/bar:ay-max-w-[80px] md:group-hover/bar:ay-ml-1.5">
                SCOPRI DI PIÙ
              </span>
            </button>
          )}

          {/* Animation Trigger - only shows if product has trigger */}
          {hasTrigger && (
            <button
              onClick={handleTriggerAnimation}
              className="ay-hidden md:ay-flex ay-items-center ay-h-7 md:ay-h-8 ay-w-10 md:ay-w-auto ay-px-1.5 md:ay-px-2 ay-rounded hover:ay-bg-black/5"
              style={{ transition: "background-color 0.15s ease" }}
              title="Avvia Animazione"
            >
              <svg
                className="ay-w-5 ay-h-5 ay-text-gray-700 ay-flex-shrink-0"
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
              <span className="ay-bar-label ay-text-[10px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 md:group-hover/bar:ay-max-w-[80px] md:group-hover/bar:ay-ml-1.5">
                ANIMAZIONE
              </span>
            </button>
          )}

          {/* Rotate - label shows on bar hover */}
          <button
            onClick={playRotation}
            className="ay-hidden md:ay-flex ay-items-center ay-h-7 md:ay-h-8 ay-w-10 md:ay-w-auto ay-px-1.5 md:ay-px-2 ay-rounded hover:ay-bg-black/5"
            style={{ transition: "background-color 0.15s ease" }}
            title="Ruota"
          >
            <img
              src={icon_autoRotate}
              alt="Ruota"
              className="ay-w-5 ay-h-5 ay-flex-shrink-0"
            />
            <span className="ay-bar-label ay-text-[10px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 md:group-hover/bar:ay-max-w-[50px] md:group-hover/bar:ay-ml-1.5">
              RUOTA
            </span>
          </button>

          {/* Views / Viste control - label shows on bar hover */}
          <div ref={perspectiveMenuRef} className="ay-relative">
            <button
              onClick={() => setShowViewMenu(!showViewMenu)}
              className="ay-flex ay-items-center ay-h-7 md:ay-h-8 ay-w-10 md:ay-w-auto ay-px-1.5 md:ay-px-2 ay-rounded hover:ay-bg-black/5"
              style={{ transition: "background-color 0.15s ease" }}
              title="Viste"
            >
              <img
                src={icon_viste}
                alt="Viste"
                className="ay-w-5 ay-h-5 ay-flex-shrink-0"
              />
              <span className="ay-bar-label ay-text-[10px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 md:group-hover/bar:ay-max-w-[40px] md:group-hover/bar:ay-ml-1.5">
                VISTE
              </span>
            </button>
          </div>

          {/* Zoom +/- (text) */}
          <div className="ay-flex ay-items-center ay-h-7 md:ay-h-8">
            <button
              onClick={zoomIn}
              className="ay-flex ay-items-center ay-justify-center ay-h-7 md:ay-h-8 ay-w-7 md:ay-w-8 ay-rounded-l hover:ay-bg-black/5 ay-text-lg md:ay-text-xl ay-font-light"
              style={{ transition: "background-color 0.15s ease" }}
              title="Zoom In"
            >
              +
            </button>
            <button
              onClick={zoomOut}
              className="ay-bar-label ay-flex ay-items-center ay-justify-center ay-h-7 md:ay-h-8 ay-w-7 md:ay-w-0 md:ay-overflow-hidden md:group-hover/bar:ay-w-8 ay-rounded-r hover:ay-bg-black/5 ay-text-lg md:ay-text-xl ay-font-light"
              title="Zoom Out"
            >
              −
            </button>
          </div>

          <button
            onClick={rulers}
            className="ay-flex ay-items-center ay-h-7 md:ay-h-8 ay-w-10 md:ay-w-auto ay-px-1.5 md:ay-px-2 ay-rounded hover:ay-bg-black/5"
            style={{ transition: "background-color 0.15s ease" }}
            title="Misure"
          >
            <img
              src={icon_measure}
              alt="Misure"
              className="ay-w-5 ay-h-5 ay-flex-shrink-0"
            />
            <span className="ay-bar-label ay-text-[10px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 md:group-hover/bar:ay-max-w-[50px] md:group-hover/bar:ay-ml-1.5">
              MISURE
            </span>
          </button>

          {/* AR - label shows on bar hover */}
          <button
            onClick={() => {
              const arButton = document.querySelector(".arButtonContainer")
              if (arButton) arButton.click()
            }}
            className="ay-flex ay-items-center ay-h-7 md:ay-h-8 ay-w-10 md:ay-w-auto ay-px-1.5 md:ay-px-2 ay-rounded hover:ay-bg-black/5"
            style={{ transition: "background-color 0.15s ease" }}
            title="Realtà Aumentata"
          >
            <img
              src={icon_vr}
              alt="AR"
              className="ay-w-5 ay-h-5 ay-flex-shrink-0"
            />
            <span className="ay-bar-label ay-text-[10px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 md:group-hover/bar:ay-max-w-[30px] md:group-hover/bar:ay-ml-1.5">
              AR
            </span>
          </button>
        </div>

        {/* Extra buttons - inside same flex container, hidden when sidebar is visible */}
        <div
          className={`ay-flex ay-items-center ay-gap-2 ${
            isSidebarVisible
              ? "ay-w-0 ay-opacity-0 ay-pointer-events-none ay-overflow-hidden"
              : "ay-w-auto ay-opacity-100"
          }`}
          style={{
            transition:
              "width 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s cubic-bezier(0.16, 1, 0.3, 1)",
          }}
        >
          {/* Screenshot */}
          <button
            onClick={getScreenshot}
            className="ay-group/btn ay-flex ay-items-center ay-h-12 ay-bg-gray-100 ay-rounded-full hover:ay-bg-gray-200 ay-overflow-hidden"
            style={{ transition: "background-color 0.15s ease" }}
            title="Scarica Immagine"
          >
            <span className="ay-flex ay-items-center ay-justify-center ay-w-12 ay-h-12 ay-flex-shrink-0">
              <img
                src={icon_screenshot}
                alt="Screenshot"
                className="ay-w-5 ay-h-5"
              />
            </span>
            <span className="ay-extra-label ay-text-[9px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 group-hover/btn:ay-max-w-[100px] group-hover/btn:ay-pr-4">
              SCARICA IMMAGINE
            </span>
          </button>

          {/* Share */}
          <button
            onClick={() => {
              navigator.clipboard.writeText(window.location.href).then(() => {
                alert("Link copiato negli appunti!")
              })
            }}
            className="ay-group/btn ay-flex ay-items-center ay-h-12 ay-bg-gray-100 ay-rounded-full hover:ay-bg-gray-200 ay-overflow-hidden"
            style={{ transition: "background-color 0.15s ease" }}
            title="Condividi"
          >
            <span className="ay-flex ay-items-center ay-justify-center ay-w-12 ay-h-12 ay-flex-shrink-0">
              <img src={icon_share} alt="Share" className="ay-w-5 ay-h-5" />
            </span>
            <span className="ay-extra-label ay-text-[9px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 group-hover/btn:ay-max-w-[70px] group-hover/btn:ay-pr-4">
              CONDIVIDI
            </span>
          </button>

          {/* PDF */}
          <button
            onClick={() => generatePDF(false)}
            className="ay-group/btn ay-flex ay-items-center ay-h-12 ay-bg-gray-100 ay-rounded-full hover:ay-bg-gray-200 ay-overflow-hidden"
            style={{ transition: "background-color 0.15s ease" }}
            title="Scarica PDF"
          >
            <span className="ay-flex ay-items-center ay-justify-center ay-w-12 ay-h-12 ay-flex-shrink-0">
              <img src={icon_pdf} alt="PDF" className="ay-w-5 ay-h-5" />
            </span>
            <span className="ay-extra-label ay-text-[9px] ay-font-medium ay-uppercase ay-whitespace-nowrap ay-overflow-hidden ay-max-w-0 group-hover/btn:ay-max-w-[80px] group-hover/btn:ay-pr-4">
              SCARICA PDF
            </span>
          </button>
        </div>
      </div>

      {/* Views dropdown menu - positioned fixed for both mobile and desktop */}
      {showViewMenu && (
        <div
          ref={perspectiveMenuRef}
          className="ay-fixed ay-left-4 md:ay-left-1/2 ay-bottom-[calc(5vh+180px)] md:ay-bottom-24 md:ay-transform md:ay--translate-x-1/2 ay-bg-white ay-shadow-xl ay-rounded-lg ay-py-2 ay-z-50 ay-max-h-80 ay-overflow-y-auto ay-min-w-[200px]"
        >
          <div className="ay-flex ay-flex-col">
            {(() => {
              const { categories, presetViews } = getViewCategories()

              return Object.entries(categories).map(([category, viewKeys]) => (
                <React.Fragment key={category}>
                  <div className="ay-text-[9px] ay-font-bold ay-px-4 ay-py-1 ay-bg-gray-100">
                    {category}
                  </div>
                  {Array.isArray(viewKeys) && viewKeys.length > 0
                    ? typeof viewKeys[0] === "string"
                      ? viewKeys.map((viewKey) => (
                          <button
                            key={viewKey}
                            onClick={() => {
                              handleCameraViewClick(presetViews[viewKey])
                              setIsMobileMenuOpen(false)
                            }}
                            className="ay-text-left ay-text-sm ay-px-4 ay-py-2 hover:ay-bg-gray-100 ay-text-[8px] ay-min-w-[140px] ay-whitespace-nowrap"
                          >
                            {presetViews[viewKey].label}
                          </button>
                        ))
                      : viewKeys
                          .filter((view) => view && view.label)
                          .map((view, index) => (
                            <button
                              key={`${view.source}-${index}-${view.label}`}
                              onClick={() => {
                                handleCameraViewClick(view)
                                setIsMobileMenuOpen(false)
                              }}
                              className="ay-text-left ay-text-sm ay-px-4 ay-py-2 hover:ay-bg-gray-100 ay-text-[8px] ay-min-w-[140px] ay-whitespace-nowrap ay-flex ay-items-center ay-justify-between"
                              title={`Vista ${view.sourceType}: ${
                                view.label
                              }${view.animate ? " (animata)" : ""}`}
                            >
                              <span>{view.label}</span>
                              <div className="ay-flex ay-items-center ay-gap-1 ay-ml-2">
                                {view.source === "family" && (
                                  <span className="ay-text-[6px] ay-bg-green-100 ay-text-green-600 ay-px-1 ay-rounded">
                                    F
                                  </span>
                                )}
                                {view.source === "product" && (
                                  <span className="ay-text-[6px] ay-bg-blue-100 ay-text-blue-600 ay-px-1 ay-rounded">
                                    P
                                  </span>
                                )}
                                {view.animate && (
                                  <span className="ay-text-[6px] ay-bg-yellow-100 ay-text-yellow-600 ay-px-1 ay-rounded">
                                    A
                                  </span>
                                )}
                              </div>
                            </button>
                          ))
                    : null}
                </React.Fragment>
              ))
            })()}
          </div>
        </div>
      )}
    </div>
  )
}

export default Player
